import { useEffect, useState } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { MessageCircle } from "lucide-react";
import { Button, CopyButton, Modal, buttonProps } from "@a2z/ui";
import { translatorFor, useI18n, useTranslate } from "@/lib/i18n";
import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type {
  DispatchTrackingLinkResponse,
  DispatchTrackingLinkView,
  DispatchTrackingSharedResponse,
} from "@/lib/api/dispatchTypes";
import { useErrorMessage } from "@/shared/errors";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { useSession } from "@/modules/auth/useSession";
import { clockTime } from "@/modules/pos/lib/dispatchBoard";
import { dispatchQueryKeys } from "@/modules/pos/lib/useDispatchBoard";
import { useDispatchCommand } from "@/modules/pos/lib/useDispatchCommand";
import { customerWhatsappDigits, trackingWhatsappUrl } from "@/modules/pos/lib/trackingShare";

/**
 * The bill's tracking link as this till last saw it (T9/T10 answers). Never
 * fetched on its own — reading it is T9, which mints — only written by the
 * share sheet, so the floor's button can say «shared» after a share.
 */
export const trackingLinkQueryKey = (invoiceId: string) =>
  [...dispatchQueryKeys.all, "tracking-link", invoiceId] as const;

/**
 * The customer's WhatsApp text: ALWAYS Arabic (MSA), whatever the till's UI
 * language, to match the `/ar/` link (P16 rework L2), and plain text — no
 * bidi isolate controls, which a chat would carry along invisibly.
 */
const toCustomer = translatorFor("ar");
const BIDI_CONTROLS = /[\u2066-\u2069\u202a-\u202e]/g;

export function customerShareMessage(input: { sequence: number; store: string; url: string }): string {
  return toCustomer((m) => m.dispatch.shareMessage, {
    sequence: input.sequence,
    store: input.store.replace(BIDI_CONTROLS, ""),
    url: input.url,
  }).replace(BIDI_CONTROLS, "");
}

/**
 * When the link ends: the time, plus the weekday when that is not today
 * (the 24 h cap usually lands tomorrow). QA-L2: two sentences, so the Arabic
 * reads «حتى يوم الاثنين الساعة 09:03», never «الساعة الاثنين».
 */
export function expiryParts(
  iso: string,
  locale: string,
  now: Date = new Date(),
): { day: string | null; time: string } {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return { day: null, time: "" };
  const time = clockTime(iso, locale);
  if (at.toDateString() === now.toDateString()) return { day: null, time };
  const day = new Intl.DateTimeFormat(locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB", { weekday: "long" }).format(at);
  return { day, time };
}

function useKnownTrackingLink(invoiceId: string) {
  return useQuery<DispatchTrackingLinkView | null>({
    queryKey: trackingLinkQueryKey(invoiceId),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * «مشاركة رابط التتبّع» — sharing a delivery's tracking link from the till
 * (driver backend B4 · P16; plan D-26). Mounted by the floor's dispatch layer
 * (`useFloorDispatch`) beside the bag's dispatch action, for
 * `RESTAURANT_DISPATCH_MANAGE` only, from the driver's accept until the bag is
 * at the door.
 *
 * Opening the sheet calls T9 (idempotent: mints the link or returns the one
 * the accept minted). The cashier then sends it from the restaurant's OWN
 * WhatsApp — a `wa.me` link addressed to the bill's customer phone, with the
 * localized message — or copies it; either records the share with T10 (an
 * idempotent command, `useDispatchCommand`), which turns the driver app's
 * line to «shared with the customer». The backend never sends the link.
 *
 * The sheet shows the link and its state to the cashier only; nothing here
 * goes to the customer except what the cashier sends.
 */
export function TrackingLinkShare({
  invoiceId,
  sequence,
  customerPhone,
  look,
}: {
  invoiceId: string;
  sequence: number;
  customerPhone: string | null;
  look: "paper" | "classic";
}) {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const known = useKnownTrackingLink(invoiceId).data ?? null;
  const shared = known?.status === "SENT";
  return (
    <>
      <button
        type="button"
        data-testid={`dispatch-share-${sequence}`}
        data-shared={shared ? "true" : "false"}
        onClick={() => setOpen(true)}
        className={[
          look === "paper"
            ? "w-full rounded-[var(--radius-control)] border bg-[color:var(--pos-tile)] px-2.5 py-[6px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors hover:text-[color:var(--text)]"
            : "shrink-0 rounded-[var(--radius-control)] border bg-[color:var(--pos-tile)] px-3 py-2 text-[length:var(--font-size-2xs)] font-extrabold transition-colors hover:border-[color:var(--accent)] hover:text-[color:var(--text)]",
          shared
            ? "border-[color:var(--success)] text-[color:var(--success)]"
            : look === "paper"
              ? "border-[color:var(--pos-tile-br,var(--border-strong))] text-[color:var(--text-secondary)]"
              : "border-[color:var(--border-strong)] text-[color:var(--text-secondary)]",
        ].join(" ")}
      >
        {shared ? t((m) => m.dispatch.shareActionShared) : t((m) => m.dispatch.shareAction)}
      </button>
      {open ? (
        <TrackingLinkShareSheet
          invoiceId={invoiceId}
          sequence={sequence}
          customerPhone={customerPhone}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

export function TrackingLinkShareSheet({
  invoiceId,
  sequence,
  customerPhone,
  onClose,
}: {
  invoiceId: string;
  sequence: number;
  customerPhone: string | null;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const { apiFetch } = usePrivateApi();
  const { data: session } = useSession();
  const { send } = useDispatchCommand();
  const [link, setLink] = useState<DispatchTrackingLinkView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [markFailure, setMarkFailure] = useState<string | null>(null);

  // T9 once per opening: mint or read the bill's link.
  useEffect(() => {
    let live = true;
    apiFetch<DispatchTrackingLinkResponse>(endpoints.restaurant.dispatchTrackingLink(invoiceId), {
      method: "POST",
    })
      .then((result) => {
        if (!live) return;
        setLink(result.link);
        queryClient.setQueryData(trackingLinkQueryKey(invoiceId), result.link);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setFailure(
          error instanceof ApiError && error.code === "DISPATCH_INVOICE_NOT_DELIVERABLE"
            ? t((m) => m.dispatch.shareErrorNotDeliverable)
            : errorMessage(error),
        );
      });
    return () => {
      live = false;
    };
    // One read per opening; `t`/`errorMessage` changing must not re-mint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  /** T10 after the cashier sent or copied the link. Same key = same command id on a retry. */
  const markShared = async () => {
    setMarkFailure(null);
    try {
      const result = await send<DispatchTrackingSharedResponse>(
        `tracking-shared:${invoiceId}`,
        endpoints.restaurant.dispatchTrackingLinkShared(invoiceId),
        {},
      );
      setLink(result.link);
      queryClient.setQueryData(trackingLinkQueryKey(invoiceId), result.link);
    } catch (error) {
      setMarkFailure(t((m) => m.dispatch.shareMarkFailed, { error: errorMessage(error) }));
    }
  };

  const digits = customerWhatsappDigits(customerPhone);
  const store = session?.seller.storeName ?? "";
  const message = link ? customerShareMessage({ sequence, store, url: link.url }) : "";
  const whatsappHref = link && digits ? trackingWhatsappUrl(digits, message) : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={t((m) => m.dispatch.shareSheetTitle, { sequence })}
      closeLabel={t((m) => m.common.close)}
      data-testid="tracking-share-sheet"
      footerActions={
        <Button variant="ghost" size="touch" onClick={onClose} data-testid="tracking-share-close">
          {t((m) => m.common.close)}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[length:var(--font-size-xs)] font-semibold text-[color:var(--text-secondary)]">
          {t((m) => m.dispatch.shareIntro)}
        </p>
        {failure ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] bg-[color:var(--danger-100)] px-3 py-2 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--danger)]"
            data-testid="tracking-share-error"
          >
            {failure}
          </p>
        ) : !link ? (
          <p role="status" className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
            {t((m) => m.dispatch.shareLoading)}
          </p>
        ) : (
          <>
            <p
              dir="ltr"
              className="break-all rounded-[var(--radius-card)] border border-[color:var(--border-light)] p-3 text-xs"
              data-testid="tracking-share-url"
            >
              {link.url}
            </p>
            <p
              className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
              data-testid="tracking-share-status"
              data-status={link.status}
            >
              {link.status === "SENT" && link.sharedAt
                ? t((m) => m.dispatch.shareStatusSent, { time: clockTime(link.sharedAt, locale) })
                : t((m) => m.dispatch.shareStatusNotSent)}
              {" · "}
              {(() => {
                const { day, time } = expiryParts(link.expiresAt, locale);
                return day
                  ? t((m) => m.dispatch.shareExpiresDay, { day, time })
                  : t((m) => m.dispatch.shareExpires, { time });
              })()}
            </p>
            {markFailure ? (
              <p role="alert" className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--danger)]" data-testid="tracking-share-mark-error">
                {markFailure}
              </p>
            ) : null}
            {!digits ? (
              <p className="text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]" data-testid="tracking-share-no-phone">
                {t((m) => m.dispatch.shareNoPhone)}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {whatsappHref ? (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tracking-share-whatsapp"
                  onClick={() => void markShared()}
                  {...buttonProps({ variant: "primary", size: "md" })}
                >
                  <MessageCircle size={16} strokeWidth={1.8} aria-hidden="true" className="me-1.5" />
                  {t((m) => m.dispatch.shareWhatsapp)}
                </a>
              ) : null}
              <CopyButton
                text={link.url}
                label={t((m) => m.dispatch.shareCopy)}
                copiedLabel={t((m) => m.dispatch.shareCopied)}
                failedLabel={t((m) => m.dispatch.shareCopyFailed)}
                size="md"
                testId="tracking-share-copy"
                onCopied={() => void markShared()}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
